// useDeposit — the deposit address, once, for the Deposit tab.
//
// MONEY PATH. Whatever the tab draws, these are the only values it draws from:
// the wallet, its deposit address, the groups the address is cut in, what the
// QR says, and Copy. Nothing derives an address of its own.
//
// - The wallet is one of the account's live wallets, CHOSEN in the tab
//   (operator, 2026-10-18: "a wallet choice in Deposit: YES"); the primary
//   (useActiveWallet) until another is chosen, and again when the dialog
//   opens anew or the chosen one leaves the list. The choice is this hook's
//   own state: it asks nothing of the server and changes no wallet.
// - A newly generated wallet has no on-chain account yet. Deposits must use
//   the non-bounceable UQ form so the first inbound transfer cannot bounce.
//   The local converter (byte-equivalent to @ton/core, pinned by
//   tonRawAddress.test.ts) keeps the TON stack out of the boot chunk. A stored
//   address the converter does not take is shown and copied as it is stored.
//   The same derivation for every wallet, chosen or not.
// - The groups are the address cut in fours and nothing else: drawing only.
// - The QR says the address that is shown and copied (the same string, behind
//   TON's documented transfer scheme: depositQrModules.ts), and only when
//   that is a TON address. No wallet: no QR. An address shown as stored: no
//   QR — a code that scans must not lead nowhere.
// - Copy writes the address, whole: the product's one copy answer
//   (ui/copyToast) — only once the address is really in the clipboard, never
//   on the press alone, the toast says so; the control itself never changes.

import { useEffect, useRef, useState } from 'react'
import type { UserlandWallet } from '../../api/userland'
import { addressToFriendlyForm } from '../../lib/tonRawAddress'
import { useActiveWallet, useWallets, useWalletsStatus } from '../../wallet/WalletsContext'
import { useCopyToast } from '../ui/copyToast'
import { depositLink } from './depositQrModules'

export interface AddressGroup {
  /** where the group starts in the address: its key */
  at: number
  text: string
}

export interface Deposit {
  /** the wallet list has not answered yet: there is no wallet to show, and it
   *  is NOT yet known that the account has none */
  loading: boolean
  wallet: UserlandWallet | null
  /** the wallets the address may be shown of: the live ones, the primary
   *  first; empty with no primary */
  choices: UserlandWallet[]
  /** show the address of the wallet with this id */
  choose: (id: string) => void
  /** what is shown, read out and copied; null with no wallet */
  address: string | null
  groups: AddressGroup[]
  /** what the QR says; null when there is nothing a wallet could send to */
  qrText: string | null
  /** undefined with no address: the control is disabled */
  copy: (() => void) | undefined
  /** what a screen reader is told inside the dialog once the address is on
   *  the clipboard (the toast stands outside the modal dialog, where it is
   *  not read out); empty otherwise */
  announced: string
}

/** The wallets a deposit address may be shown of: the account's live ones
 *  (not archived, not deleted), the primary first. None without a primary —
 *  the tab then says there is no wallet, as before. */
export function depositChoices(
  wallets: readonly UserlandWallet[],
  primary: UserlandWallet | null
): UserlandWallet[] {
  if (!primary) return []
  const live = wallets.filter(
    (w) => !w.archived && w.activation_status !== 'deleted' && w.id !== primary.id
  )
  return [primary, ...live]
}

export function useDeposit(): Deposit {
  const primary = useActiveWallet()
  const { wallets } = useWallets()
  // An empty list that has not answered is not "no wallet": the tab must not
  // tell a person who has a wallet to create one (it did, until the list came).
  const status = useWalletsStatus()
  const choices = depositChoices(wallets, primary)
  const [chosenId, setChosenId] = useState<string | null>(null)
  // the chosen one while it is in the list; the primary otherwise
  const wallet = choices.find((w) => w.id === chosenId) ?? primary
  const loading = wallet === null && status === 'loading'
  const friendly = wallet ? addressToFriendlyForm(wallet.address, false) : null
  const address = wallet ? (friendly ?? wallet.address) : null
  const groups = address
    ? Array.from(address.matchAll(/.{1,4}/g), (m) => ({ at: m.index, text: m[0] }))
    : []
  const qrText = address !== null && friendly !== null ? depositLink(address) : null

  const copyToast = useCopyToast()
  const [announced, setAnnounced] = useState('')
  const announceTimer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(announceTimer.current), [])
  // A refused write (permission, focus) says nothing.
  const copy = address
    ? () =>
        void copyToast(address, 'Deposit address copied').then((ok) => {
          if (!ok) return
          setAnnounced('Deposit address copied')
          window.clearTimeout(announceTimer.current)
          announceTimer.current = window.setTimeout(() => setAnnounced(''), 1400)
        })
    : undefined
  const choose = (id: string) => {
    if (id === wallet?.id) return
    setChosenId(id)
  }

  return { loading, wallet, choices, choose, address, groups, qrText, copy, announced }
}
